import { useState, useEffect } from 'react';
import Highlight from '@App/model/similarSearch/Highlight';
import Waveform from '@App/components/model/Waveform';
import Loader from '@App/components/ui/Loader';

interface Props {
  waveformId?: string | undefined
  reference: string
  audioFileUrl: string | null
  waveformUrl: string | null
  /** PSE PNG fallback, used when the JSON peaks cannot be parsed (see adaptWaveform). */
  waveformImageUrl?: string | null
  progress: number | null
  duration: number
  light?: boolean
  highlights?: Highlight[]
  cursor?: boolean
  onSegmentChange?: () => void
  setIsPlaying?: () => void
}

/**
 * Waveform peaks in the format consumed by {@link Waveform}: a flat array of signed integers
 * read in interleaved pairs (the renderer draws a vertical line between each pair, so the
 * min/max order within a pair does not matter visually).
 */
type MioozWaveform = number[];

/**
 * PSE (BBC audiowaveform / waveform-data.js v2) JSON peaks: a flat `data` array read in
 * interleaved `[min, max, min, max, …]` pairs, `length` being the number of pairs.
 *
 * @see https://github.com/bbc/audiowaveform
 */
interface PseWaveform {
  version: number
  channels: number
  length: number
  bits: number
  data: number[]
}

// Number of rendered pairs, matching the backend music waveform resampling
// (WaveformRepository::getPoints() = 150). PSE ships thousands of peaks, which would otherwise
// render a multi-thousand-pixel SVG that overflows the row.
//
// This is an exact count, not a target: {@link Waveform} sizes its SVG as `pairs × SPACING`, so the
// pair count IS the on-screen width. Resampling by a rounded block size (as the backend does, where
// every track is long enough for the rounding to vanish) leaves short sounds with fewer pairs — a
// 4s sound came out 121 pairs against a 62s one's 150, i.e. a 363px waveform next to a 450px one in
// the same list, ending short of its own duration label. Worse, the seeker derives the clicked
// position from the box width, so a waveform narrower than its row makes seeking land off-target.
const POINTS = 150;

/** On-screen width of a rendered waveform, and therefore of the PNG that stands in for one. */
const WIDTH = POINTS * Waveform.SPACING;

function isPseWaveform(value: unknown): value is PseWaveform {
  return typeof value === 'object'
    && value !== null
    && Array.isArray((value as PseWaveform).data);
}

/**
 * Normalize whatever a `waveformUrl` returns into the Miooz {@link Waveform} format.
 *
 * - A plain array is already in the expected format (Miooz music tracks) and passed through.
 * - A PSE audiowaveform v2 object is resampled to exactly POINTS pairs, each keeping the extremes of
 *   its source block.
 * - Anything else returns `null`, signalling the caller to fall back to the PNG image.
 */
export function adaptWaveform(payload: unknown): MioozWaveform | null {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!isPseWaveform(payload)) {
    return null;
  }

  const data = payload.data;
  const pairCount = typeof payload.length === 'number' && payload.length > 0
    ? payload.length
    : Math.floor(data.length / 2);

  if (pairCount <= 0) {
    return null;
  }

  // Never read past the pairs actually shipped, whatever `length` claims.
  const pairs = Math.min(pairCount, Math.floor(data.length / 2));

  // Cut the source into POINTS consecutive blocks and keep the extremes of each — the lowest min and
  // the highest max — rather than averaging them.
  //
  // The backend averages, and this mirrored it until measurement showed what that costs a sound
  // effect. A block spans `pairs / POINTS` source pairs, so it grows with duration: on a 62s crowd
  // applause a block covers 77 pairs, and averaging a transient with the calm around it destroyed 86%
  // of the peak (74% on a door slam, 71% on a thunder rumble, 0% on a 0.9s footstep where a block is
  // a single pair). Since the renderer normalizes on the waveform's own peak, the damage does not
  // read as "too small" but as a flat uniform block that no longer says anything about what will be
  // heard. Music escapes this only because a three-minute track has enough pairs to spare.
  //
  // Block bounds are computed per point rather than from a single rounded block size, which is what
  // keeps the count exact: every point gets at least one source pair, and a sound shorter than POINTS
  // pairs is stretched by repetition, exactly as a short music track would be.
  //
  // Kept in source [min, max] order: the renderer draws a vertical line between the pair, so the
  // order within it is visually irrelevant.
  const adapted: number[] = [];

  for (let point = 0; point < POINTS; point++) {
    const start = Math.min(Math.floor((point * pairs) / POINTS), pairs - 1);
    const end = Math.max(Math.floor(((point + 1) * pairs) / POINTS), start + 1);
    let min = data[start * 2];
    let max = data[start * 2 + 1];

    for (let pair = start + 1; pair < end; pair++) {
      min = Math.min(min, data[pair * 2]);
      max = Math.max(max, data[pair * 2 + 1]);
    }

    adapted.push(min, max);
  }

  return adapted;
}

export default function WaveformByUrl({
  waveformId,
  reference,
  audioFileUrl,
  waveformUrl,
  waveformImageUrl = null,
  progress,
  duration,
  light = false,
  highlights = [],
  cursor = false,
  onSegmentChange,
  setIsPlaying,
}: Props) {
  const [loading, setLoading] = useState<boolean>(false);
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [useImageFallback, setUseImageFallback] = useState<boolean>(false);

  /**
   * Fetches and normalizes the waveform data.
   */
  useEffect(() => {
    setUseImageFallback(false);

    if (!waveformUrl) {
      return;
    }

    const controller = new AbortController();
    setLoading(true);

    fetch(waveformUrl, { signal: controller.signal })
      .then(res => res.json())
      .then(res => {
        const adapted = adaptWaveform(res);

        if (adapted === null) {
          setUseImageFallback(true);

          return;
        }

        setWaveformData(adapted);
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.error('Failed to fetch waveform:', err);
          setUseImageFallback(true);
        }
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [waveformUrl]);

  // PNG fallback: only when the JSON is unusable and a permanent image URL is available. It is given
  // the width a rendered waveform would have: left to its own intrinsic size (hundreds of pixels
  // wider than the row) it pushes the actions column out of reach, and any other width would put the
  // seeker's box and the drawn waveform out of step.
  if (useImageFallback && waveformImageUrl) {
    return <img className={`waveform waveform--image ${light ? 'waveform--light' : ''}`} style={{ width: WIDTH }} src={waveformImageUrl} alt="" />;
  }

  return <>
    {waveformData && <Waveform
      data={waveformData}
      id={waveformId ?? reference}
      audioFileUrl={audioFileUrl}
      duration={duration}
      highlights={highlights}
      progress={progress}
      light={light}
      cursor={cursor}
      onSegmentChange={onSegmentChange}
      setIsPlaying={setIsPlaying}
    />}
    {loading && <Loader/>}
  </>;
}
